import type { Referanse } from "../types/referanse";
import type { Oppgave } from "../types/oppgave";

import type { Intensjon } from "../enums/intensjon";
import type { ResourceIdType } from "../enums/resourceIdType";

import { erSkjemaOppgave, erVerktoyOppgave } from "./oppgaveTypeUtils";
import { ElementType } from "../enums/elementType";
import type { HNOppgaveFrontend } from "HNResources";
import type { BreadcrumbLink } from "@helsenorge/core-framework/breadcrumb";
import { OPPGAVE_RETUR_URL_TEKST } from "HNEntities/url-types";

export const getRootUrl = (oppgave: Oppgave): string => {
  if (oppgave) {
    if (erSkjemaOppgave(oppgave)) {
      return "/skjemautfyller";
    }

    if (erVerktoyOppgave(oppgave)) {
      return "/verktoy";
    }
  }
  return "/samtykkeoppgave";
};

export const redirectTilOppgave = (oppgave: Oppgave | undefined): void => {
  if (oppgave) {
    if (oppgave.InstantiatesUri) {
      window.open(oppgave.InstantiatesUri, "_blank");
    } else {
      const params = getRedirectUrlParams(oppgave);

      const queryParams = Object.entries(params).map(([key, value]) => {
        return `${key}=${encodeURIComponent(value)}`;
      });
      const rootUrl = getRootUrl(oppgave);

      const path = erVerktoyOppgave(oppgave) ? `/${oppgave.ResourceId}` : "";
      const fullUrl = `${rootUrl}${path}?${queryParams.join("&")}`;

      window.location.assign(fullUrl);
    }
  }
};

type RedirectUrlParams =
  | {
      oppgaveId: string;
      retururl: string;
      retururltekst: string;
      intensjon: Intensjon;
    }
  | {
      resourceId?: string;
      resourceIdType?: ResourceIdType;
      requesterId: string;
      requesterIdType: number;
      taskId: string;
      returURL: string;
      contextId?: string;
    };

const getRedirectUrlParams = (oppgave: Oppgave): RedirectUrlParams => {
  if (erVerktoyOppgave(oppgave)) {
    return {
      oppgaveId: oppgave.Id,
      retururl: window.location.pathname,
      retururltekst: OPPGAVE_RETUR_URL_TEKST,
      intensjon: oppgave.Intensjon,
    };
  } else {
    return {
      ...(oppgave.ResourceId && { resourceId: oppgave.ResourceId }), // skjemaoppgave med vedlagt skjemadefinisjon inneholder ikke ResourceId
      ...(oppgave.ResourceIdType && { resourceIdType: oppgave.ResourceIdType }),
      requesterId: oppgave.OpprettetAvId,
      requesterIdType: oppgave.OpprettetAvIdType,
      taskId: oppgave.Id,
      returURL: window.location.pathname,
      retururltekst: OPPGAVE_RETUR_URL_TEKST,
      ...(oppgave.OppgaveSvar?.Id && { contextId: oppgave.OppgaveSvar?.Id }), // contextId blir bare brukt for skjemaoppgave
    };
  }
};

export const redirectTilHelsenorgeElement = (referanse: Referanse): string => {
  switch (referanse.Type) {
    case ElementType.Henvisning: {
      return `${window.location.origin}/henvisninger/henvisning/${referanse.Id}`;
    }
    case ElementType.Timeavtale: {
      return `${window.location.origin}/timeavtaler/avtale/${referanse.Id}`;
    }
    default:
      return "/";
  }
};

export const getBreadcrumbLink = (
  pathname: string,
  resources: HNOppgaveFrontend | undefined,
): BreadcrumbLink | undefined => {
  // Hvis vi er på en oppgavedetalj-side, vis breadcrumb-link til oppgaveoversikten
  if (pathname.match("/oppgaver/")) {
    return {
      name: resources?.breadcrumb_oppgaver || "",
      url: "/oppgaver",
      renderAsRouterLink: true,
    };
  } else {
    return;
  }
};
